<template>
  <div class="flex items-start gap-1 hide-scrollbar" :class="!disableFlexWrap && 'flex-wrap'">
    <button
      v-for="suggestion in firstFourSuggestions"
      :key="suggestion.id"
      :class="buttonClass"
      :style="buttonStyle"
      @click="() => implementSuggestion(suggestion)"
      type="button">
      {{ suggestion.copy }}
    </button>
  </div>
</template>

<script setup>
import {computed, ref, watch} from "vue";
import {debounce} from "../marketing-site/lightweight-debounce";
import {getLevenshteinDistance} from "../helpers/LevenshteinDistance";

const emits = defineEmits(["update:modelValue"]);
const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
  buttonClass: {
    type: String,
    default: "bg-slate-200 text-slate-600 text-xs font-medium px-2 py-1 rounded-lg",
  },
  buttonStyle: {
    type: String,
    default: "",
  },
  maxSuggestions: {
    type: Number,
    default: 4,
  },
  disableFlexWrap: {
    type: Boolean,
    default: false,
  },
});

const debouncedInput = ref("");
const debounceUpdateInput = debounce((value) => debouncedInput.value = value, 100);

const TOP_EMAIL_PROVIDERS = [
  "@gmail.com",
  "@outlook.com",
  "@icloud.com",
  "@yahoo.com",
  "@hotmail.com",
  "@aol.com",
  "@protonmail.com",
  "@zoho.com",
];

const SUGGESTION_TYPE_AUTOCOMPLETE = "autocomplete";
const SUGGESTION_TYPE_TYPO = "typo";
const SUGGESTION_TYPE_DOMAIN = "domain";

const makeSuggestion = (type, value) => {
  let copy, rank, handler;

  switch (type) {
  case SUGGESTION_TYPE_AUTOCOMPLETE:
    copy = value;
    rank = 1;
    handler = (val) => {
      const atIndex = val.indexOf("@");
      return atIndex !== -1 ? val.slice(0, atIndex + 1) + value : val + value;
    };
    break;

  case SUGGESTION_TYPE_TYPO:
    rank = 2;
    copy = "Do you mean @" + value + "?";
    handler = (val) => val.replace(/@[^@]+$/, "@" + value);
    break;

  case SUGGESTION_TYPE_DOMAIN:
    rank = 3;
    copy = "Do you mean ." + value + "?";
    handler = (val) => val.replace(/\.[a-z]+$/, "." + value);
    break;
  }

  const id = Math.random().toString(36).slice(2);

  return {id, type, rank, copy, handler};
};

const detectTypo = (value) => {
  const indexOfAt = value.indexOf("@");
  const domainPart = value.slice(indexOfAt);
  if (!domainPart) return null;

  if (TOP_EMAIL_PROVIDERS.includes(domainPart)) return null;

  for (const provider of TOP_EMAIL_PROVIDERS) {
    const d = getLevenshteinDistance(provider, domainPart);
    if (d <= 2) return provider.slice(1);
  }

  return null;
};

const popularTLDS = [
  "com", "co", "org", "net", "io", "ai", "online", "store", "us"
];

const detectDomainTypo = (input) => {
  const TLDPattern = /\.[a-z]+$/;
  const TLDMatch = input.match(TLDPattern);
  if (!TLDMatch) return null;

  const TLD = TLDMatch[0].slice(1);
  if (popularTLDS.includes(TLD)) return null;

  const firstLetter = TLD[0];

  for (const popularTLD of popularTLDS) {
    if (popularTLD[0] === firstLetter) {
      return popularTLD;
    }
  }

  return "com";
};

// Compute suggestions based on user input
const suggestions = computed(() => {
  const input = debouncedInput.value;
  const atIndex = input.indexOf("@");

  if (atIndex === -1) {
    return TOP_EMAIL_PROVIDERS.map((provider) =>
      makeSuggestion(SUGGESTION_TYPE_AUTOCOMPLETE, provider)
    );
  }

  if (input.slice(atIndex + 1).includes("@")) return [];

  const domain = input.slice(atIndex + 1);
  const hasTLD = domain.includes(".");

  const allSuggestions = [];

  let typo, domainTypo = "";

  if (hasTLD) {
    typo = detectTypo(input);
    domainTypo = detectDomainTypo(input);

    if (typo) {
      allSuggestions.push(makeSuggestion(SUGGESTION_TYPE_TYPO, typo));
    } else if (domainTypo) {
      allSuggestions.push(makeSuggestion(SUGGESTION_TYPE_DOMAIN, domainTypo));
    }
  }

  const autocompleteSuggestions = TOP_EMAIL_PROVIDERS
    .filter((provider) => provider.startsWith("@" + domain) && provider !== "@" + domain && !provider.startsWith("@" + typo))
    .map((provider) => makeSuggestion(SUGGESTION_TYPE_AUTOCOMPLETE, provider));

  return allSuggestions.concat(autocompleteSuggestions);
});

const firstFourSuggestions = computed(() => {
  const copySuggestions = suggestions.value.map((suggestion) => ({...suggestion}));
  return copySuggestions.sort((a, b) => a.rank - b.rank).slice(0, props.maxSuggestions);
});

const implementSuggestion = (suggestion) => emits("update:modelValue", suggestion.handler(props.modelValue));

watch(() => props.modelValue, (newValue, oldValue) => {
  if (newValue === oldValue) return;
  if (newValue) debounceUpdateInput(newValue);
  else debouncedInput.value = "";
}, {immediate: true});
</script>
